import type { EnterpriseWorkspaceRoutes } from 'v5/core/routing/appRouting';
import type { AppRouting } from 'v5/features/core/routing';

/**
 * Bridges legacy `myRouting.forWorkspace` URL builders to the {@link EnterpriseWorkspaceRoutes}
 * shape used by enterprise routing (`appRouting.org().workspace()`).
 *
 * **Why it exists:** `useAppWorkspaceRouting()` exposes a single API for **browsing chrome**
 * (list roots, breadcrumbs). It picks this adapter on legacy URLs and enterprise builders on
 * `/w/...` URLs — based on pathname, not the feature flag.
 *
 * **Do not use for entity links.** Links to a specific record must use `entityWorkspaceRouting`
 * (driven by `enterprise_workspace_id` on the entity).
 *
 * **Transformation period:** While both URL worlds coexist, this adapter is the legacy side of
 * that union. Legacy org URLs stay `/app/:app_id/...`; enterprise URLs use
 * `/app/:app_id/w/:workspaceId/...`.
 * The same component code keeps working in both worlds as long as enterprise route entries
 * (and any dual `path` arrays on v1 route shells) register those URLs in React Router.
 *
 * **Link generation only:** Does not participate in route matching. Matching incoming URLs
 * still belongs in the router layer (e.g. enterprise routes in `Routes.jsx`, settings dual paths).
 *
 * **Removal:** Safe to delete once legacy workspace URLs are dropped and all callers use
 * `appRouting` directly without the adapter.
 */
export function adaptLegacyWorkspaceRoutingForLinks(
  legacy: ReturnType<AppRouting['forWorkspace']>
): EnterpriseWorkspaceRoutes {
  const resources = legacy.resources();
  const contacts = legacy.contacts();
  const productions = legacy.productions();
  const shortlists = legacy.shortlists();
  const callouts = legacy.callouts();
  const bookings = legacy.bookings();
  const reconciliations = legacy.reconciliations();

  return {
    root: legacy.root,
    dashboard: () => `${legacy.root()}/dashboard`,
    resources: {
      root: resources.root,
      forResourceType: resources.item
    },
    contacts: {
      root: contacts.root,
      detail: contacts.detail,
      tab: contacts.tab
    },
    productions: {
      root: productions.root,
      type: productions.type,
      detail: productions.detail,
      resourcing: productions.resourcing
    },
    shortlists: {
      root: shortlists.root,
      detail: shortlists.detail
    },
    callouts: {
      root: callouts.root,
      detail: callouts.detail,
      publicCallout: callouts.publicCallout
    },
    bookings: {
      root: bookings.root,
      type: bookings.type,
      detail: bookings.detail
    },
    calendar: {
      root: () => `${legacy.root()}/calendar`,
      resources: {
        home: () => `${legacy.root()}/calendar/resources`
      },
      productions: {
        home: () => `${legacy.root()}/calendar/productions`
      }
    },
    reconciliations: {
      root: reconciliations.root
    },
    team: () => {
      throw new Error('Legacy routing has no team scope');
    }
  };
}
